/**
 * §3 — Expanded formation library + coach-saved custom formations.
 *
 * The seed FORMATIONS in `packages/core/src/constants.ts` use a
 * normalised [0, 1] coordinate space and are consumed by the existing
 * `formationProjection.ts` in apps/web2. This module is the canonical
 * source of truth for *world-metres* formation slots — same coordinate
 * frame as `TacticsLayout.homeSpots` / `awaySpots`:
 *
 *   - x ∈ [-FIELD_WIDTH_M  / 2, +FIELD_WIDTH_M  / 2]  (sidelines)
 *   - z ∈ [-FIELD_LENGTH_M / 2, +FIELD_LENGTH_M / 2]  (-z attacking, +z own goal)
 *
 * Slots are authored for the HOME side. `mirrorFormationForAway` flips
 * along the z-axis to produce the away-side layout (no x flip — the
 * pitch is symmetric across the midline).
 *
 * These eight codes intentionally do NOT overlap with the seed
 * FORMATIONS' 11v11 entries ('4-3-3', '4-4-2', '4-2-3-1', '3-5-2') —
 * those four already ship via `formationProjection.ts`. Callers that
 * want the union should merge the two universes (the
 * `FormationLibraryDrawer` does so in its standard list).
 */

import { FIELD_LENGTH_M, FIELD_WIDTH_M } from './adapters';

/** A single role slot in world-metres pitch coordinates. */
export interface ExtendedFormationSlot {
  role: string;
  x: number;
  z: number;
}

/** A complete 11-slot formation definition. */
export interface ExtendedFormation {
  slots: ExtendedFormationSlot[];
}

// ---------------------------------------------------------------------------
// Helpers for authoring symmetric formations.
// ---------------------------------------------------------------------------

const HALF_W = FIELD_WIDTH_M / 2;   // 34
const HALF_L = FIELD_LENGTH_M / 2;  // 52.5

/** Z-bands for the home side, expressed as a fraction of the half-pitch.
 *  Positive = closer to own goal; negative = closer to attacking goal.
 *  Bands are calibrated so a back-four sits around the 18-yard area
 *  and the front line presses the opposition third. */
const Z = {
  GK: HALF_L * 0.92,        // ~+48m — on own 6-yard line
  DEF: HALF_L * 0.55,       // ~+29m — own third
  DEF_HIGH: HALF_L * 0.42,  // ~+22m — back-three pushed up
  DM: HALF_L * 0.22,        // ~+12m — defensive midfield
  MID: HALF_L * 0.05,       // ~+3m — central mid (just behind halfway)
  AM: -HALF_L * 0.15,       // ~-8m — attacking mid / no.10
  FW_DEEP: -HALF_L * 0.30,  // ~-16m — withdrawn forward / second striker
  FW: -HALF_L * 0.48,       // ~-25m — front line
};

/** X-bands. Wide = touchline pressure; channel = half-space; centre = 0. */
const X = {
  LWIDE: -HALF_W * 0.78,    // ~-27m — wing-back / winger
  LWIDE_MID: -HALF_W * 0.62,// ~-21m — wide midfielder
  LCHANNEL: -HALF_W * 0.38, // ~-13m — left half-space
  LINSIDE: -HALF_W * 0.22,  // ~-7.5m — narrow inside-left
  CENTRE: 0,
  RINSIDE: HALF_W * 0.22,
  RCHANNEL: HALF_W * 0.38,
  RWIDE_MID: HALF_W * 0.62,
  RWIDE: HALF_W * 0.78,
};

// ---------------------------------------------------------------------------
// Extended 11v11 formations (8 codes — all home-side).
// ---------------------------------------------------------------------------

/**
 * Every entry below carries exactly 11 slots in the order:
 *   GK → back line → midfield line(s) → front line.
 * Symmetry: left/right pairs use the same |x| value so flipping the
 * sign mirrors cleanly.
 */
export const EXTENDED_FORMATIONS: Record<string, ExtendedFormation> = {
  // Back three / wing-backs / two No.10s behind a lone striker.
  '3-4-2-1': {
    slots: [
      { role: 'GK',  x: X.CENTRE,   z: Z.GK },
      { role: 'CB',  x: X.LCHANNEL, z: Z.DEF_HIGH },
      { role: 'CB',  x: X.CENTRE,   z: Z.DEF_HIGH + 2 },
      { role: 'CB',  x: X.RCHANNEL, z: Z.DEF_HIGH },
      { role: 'LWB', x: X.LWIDE,    z: Z.MID + 4 },
      { role: 'CM',  x: X.LINSIDE,  z: Z.MID },
      { role: 'CM',  x: X.RINSIDE,  z: Z.MID },
      { role: 'RWB', x: X.RWIDE,    z: Z.MID + 4 },
      { role: 'CAM', x: X.LCHANNEL, z: Z.AM },
      { role: 'CAM', x: X.RCHANNEL, z: Z.AM },
      { role: 'ST',  x: X.CENTRE,   z: Z.FW },
    ],
  },

  // Christmas tree — back four, holder, two CMs, two No.10s, lone ST.
  '4-3-2-1': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'LB',  x: X.LWIDE,     z: Z.DEF },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF + 2 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF + 2 },
      { role: 'RB',  x: X.RWIDE,     z: Z.DEF },
      { role: 'CDM', x: X.CENTRE,    z: Z.DM },
      { role: 'CM',  x: X.LCHANNEL,  z: Z.MID },
      { role: 'CM',  x: X.RCHANNEL,  z: Z.MID },
      { role: 'CAM', x: X.LINSIDE,   z: Z.AM },
      { role: 'CAM', x: X.RINSIDE,   z: Z.AM },
      { role: 'ST',  x: X.CENTRE,    z: Z.FW },
    ],
  },

  // Diamond midfield — back four, single pivot, two wide CMs, AM, two STs.
  '4-1-2-1-2': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'LB',  x: X.LWIDE,     z: Z.DEF },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF + 2 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF + 2 },
      { role: 'RB',  x: X.RWIDE,     z: Z.DEF },
      { role: 'CDM', x: X.CENTRE,    z: Z.DM },
      { role: 'CM',  x: X.LWIDE_MID, z: Z.MID + 2 },
      { role: 'CM',  x: X.RWIDE_MID, z: Z.MID + 2 },
      { role: 'CAM', x: X.CENTRE,    z: Z.AM },
      { role: 'ST',  x: X.LINSIDE,   z: Z.FW },
      { role: 'ST',  x: X.RINSIDE,   z: Z.FW },
    ],
  },

  // 3-5-2 — explicit (extended-library version; seed already ships one).
  '3-5-2': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF_HIGH },
      { role: 'CB',  x: X.CENTRE,    z: Z.DEF_HIGH + 2 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF_HIGH },
      { role: 'LWB', x: X.LWIDE,     z: Z.MID + 4 },
      { role: 'CM',  x: X.LCHANNEL,  z: Z.MID },
      { role: 'CM',  x: X.CENTRE,    z: Z.MID - 2 },
      { role: 'CM',  x: X.RCHANNEL,  z: Z.MID },
      { role: 'RWB', x: X.RWIDE,     z: Z.MID + 4 },
      { role: 'ST',  x: X.LINSIDE,   z: Z.FW },
      { role: 'ST',  x: X.RINSIDE,   z: Z.FW },
    ],
  },

  // Five at the back, compact mid three, two strikers.
  '5-3-2': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'LWB', x: X.LWIDE,     z: Z.DEF + 2 },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF_HIGH + 2 },
      { role: 'CB',  x: X.CENTRE,    z: Z.DEF_HIGH + 3 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF_HIGH + 2 },
      { role: 'RWB', x: X.RWIDE,     z: Z.DEF + 2 },
      { role: 'CM',  x: X.LCHANNEL,  z: Z.MID },
      { role: 'CM',  x: X.CENTRE,    z: Z.MID - 2 },
      { role: 'CM',  x: X.RCHANNEL,  z: Z.MID },
      { role: 'ST',  x: X.LINSIDE,   z: Z.FW },
      { role: 'ST',  x: X.RINSIDE,   z: Z.FW },
    ],
  },

  // 4-1-4-1 — single pivot anchoring a flat midfield band, lone striker.
  '4-1-4-1': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'LB',  x: X.LWIDE,     z: Z.DEF },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF + 2 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF + 2 },
      { role: 'RB',  x: X.RWIDE,     z: Z.DEF },
      { role: 'CDM', x: X.CENTRE,    z: Z.DM },
      { role: 'LM',  x: X.LWIDE,     z: Z.MID },
      { role: 'CM',  x: X.LINSIDE,   z: Z.MID - 2 },
      { role: 'CM',  x: X.RINSIDE,   z: Z.MID - 2 },
      { role: 'RM',  x: X.RWIDE,     z: Z.MID },
      { role: 'ST',  x: X.CENTRE,    z: Z.FW },
    ],
  },

  // Box midfield — back four, two pivots, two No.10s, two strikers.
  '4-2-2-2': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'LB',  x: X.LWIDE,     z: Z.DEF },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF + 2 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF + 2 },
      { role: 'RB',  x: X.RWIDE,     z: Z.DEF },
      { role: 'CDM', x: X.LCHANNEL,  z: Z.DM },
      { role: 'CDM', x: X.RCHANNEL,  z: Z.DM },
      { role: 'CAM', x: X.LCHANNEL,  z: Z.AM },
      { role: 'CAM', x: X.RCHANNEL,  z: Z.AM },
      { role: 'ST',  x: X.LINSIDE,   z: Z.FW },
      { role: 'ST',  x: X.RINSIDE,   z: Z.FW },
    ],
  },

  // Back three with a front three — narrow CBs, attacking wing-backs in midfield.
  '3-4-3': {
    slots: [
      { role: 'GK',  x: X.CENTRE,    z: Z.GK },
      { role: 'CB',  x: X.LCHANNEL,  z: Z.DEF_HIGH },
      { role: 'CB',  x: X.CENTRE,    z: Z.DEF_HIGH + 2 },
      { role: 'CB',  x: X.RCHANNEL,  z: Z.DEF_HIGH },
      { role: 'LWB', x: X.LWIDE,     z: Z.MID + 4 },
      { role: 'CM',  x: X.LINSIDE,   z: Z.MID },
      { role: 'CM',  x: X.RINSIDE,   z: Z.MID },
      { role: 'RWB', x: X.RWIDE,     z: Z.MID + 4 },
      { role: 'LW',  x: X.LWIDE,     z: Z.FW + 2 },
      { role: 'ST',  x: X.CENTRE,    z: Z.FW },
      { role: 'RW',  x: X.RWIDE,     z: Z.FW + 2 },
    ],
  },
};

// ---------------------------------------------------------------------------
// Mirroring + guards.
// ---------------------------------------------------------------------------

/**
 * Flip a home-side formation across the halfway line to produce the
 * away-side layout. Negates ONLY z — away slots are authored at home
 * coordinates, so flipping z drops the away team at the opposite end
 * instead of overlapping the home team.
 *
 * We deliberately do NOT flip x. A point-reflection through the centre
 * (negating both axes) keeps L/R role labels correct from the away
 * team's own POV, but renders the away formation visibly rotated 180°
 * on the coach's single shared screen — coaches found that confusing.
 * Keeping x as-drawn reads naturally end-to-end.
 */
export function mirrorFormationForAway<T extends { role: string; x: number; z: number }>(
  positions: readonly T[],
): T[] {
  // `+ 0` normalises -0 → 0 so toEqual checks don't fail on Object.is
  // distinguishing the two zeros (a recurring papercut on this codebase
  // — see also teamShape.ts's lineHeightDeltaM handling).
  return positions.map((p) => ({ ...p, z: -p.z + 0 }));
}

// ---------------------------------------------------------------------------
// Custom (coach-saved) formations.
// ---------------------------------------------------------------------------

/** A coach-saved formation. Persisted in localStorage by
 *  `apps/web2/src/lib/customFormationsStore.ts`. */
export interface TacticsCustomFormation {
  id: string;
  name: string;
  /** ISO 8601 timestamp. */
  createdAt: string;
  slots: ExtendedFormationSlot[];
}

/** Type guard for runtime parsing — used when reading from
 *  localStorage or off the wire. Rejects anything missing the four
 *  required fields or with a malformed slots array. */
export function isTacticsCustomFormation(value: unknown): value is TacticsCustomFormation {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string') return false;
  if (typeof v.name !== 'string') return false;
  if (typeof v.createdAt !== 'string') return false;
  if (!Array.isArray(v.slots)) return false;
  return v.slots.every((s) => {
    if (!s || typeof s !== 'object') return false;
    const slot = s as Record<string, unknown>;
    return (
      typeof slot.role === 'string' &&
      typeof slot.x === 'number' &&
      typeof slot.z === 'number'
    );
  });
}

// ---------------------------------------------------------------------------
// Inference — guess the formation code from a 11-slot positions list.
// ---------------------------------------------------------------------------

/** Defender role codes seen across the seed + extended libraries. */
const DEFENDER_ROLES = new Set(['CB', 'LB', 'RB', 'SW']);
const MIDFIELDER_ROLES = new Set([
  'CM', 'CDM', 'CAM', 'LM', 'RM', 'DM', 'AM', 'M',
]);
const FORWARD_ROLES = new Set(['ST', 'CF', 'LW', 'RW', 'LF', 'RF', 'SS', 'LS', 'RS', 'F']);
/** Wing-back roles are ambiguous — 3-5-2 plays them as midfielders, 5-3-2
 *  plays them as defenders. Bucket by z-coordinate at inference time. */
const WINGBACK_ROLES = new Set(['LWB', 'RWB']);

/**
 * Heuristic mapping from a (defender-count, mid-count, forward-count)
 * triple to the closest standard formation code. Returns null when
 * the counts don't match any recognised system.
 *
 * Used by the FormationLibraryDrawer to label a saved custom layout
 * with the closest standard code ("looks like a 4-3-3") without
 * requiring the coach to tag it manually.
 */
export function inferFormationCodeFromSlots(
  slots: readonly { role: string; z?: number }[],
): string | null {
  if (!Array.isArray(slots) || slots.length !== 11) return null;

  // Collect outfield z-values to derive a midfield-defence threshold —
  // wing-backs bucket via this threshold (defender vs midfielder).
  const outfieldZs = slots
    .filter((s) => s.role.toUpperCase() !== 'GK' && typeof s.z === 'number')
    .map((s) => s.z as number);
  // Midline is the pitch centre; anything z > 0 sits on the home/defensive
  // side. A wing-back at z ≈ 12 (mid-line) is playing as a midfielder; one
  // at z ≈ 25 (in-line with the centre-backs) is a defender.
  // Threshold: 25% of the deepest outfield z. Falls back to 0 if no data.
  const maxZ = outfieldZs.length ? Math.max(...outfieldZs) : 0;
  const wbDefenderThreshold = maxZ * 0.55;

  let defenders = 0;
  let midfielders = 0;
  let forwards = 0;
  let goalkeepers = 0;
  for (const slot of slots) {
    const role = slot.role.toUpperCase();
    if (role === 'GK') {
      goalkeepers += 1;
    } else if (WINGBACK_ROLES.has(role)) {
      // Disambiguate wing-backs by depth — deep WB = defender, advanced WB = mid.
      if (typeof slot.z === 'number' && slot.z >= wbDefenderThreshold) {
        defenders += 1;
      } else {
        midfielders += 1;
      }
    } else if (DEFENDER_ROLES.has(role)) {
      defenders += 1;
    } else if (MIDFIELDER_ROLES.has(role)) {
      midfielders += 1;
    } else if (FORWARD_ROLES.has(role)) {
      forwards += 1;
    }
  }
  if (goalkeepers !== 1) return null;
  if (defenders + midfielders + forwards !== 10) return null;

  const key = `${defenders}-${midfielders}-${forwards}`;
  // Direct (d-m-f) → most-common formation code. Where two codes share
  // the same raw d-m-f triple (e.g. 4-2-3-1 and 4-1-4-1 both count as
  // 4-5-1), we prefer the more popular / recognisable system.
  const DIRECT: Record<string, string> = {
    '4-3-3': '4-3-3',
    '4-4-2': '4-4-2',
    '4-5-1': '4-2-3-1',   // shared by 4-2-3-1 / 4-1-4-1 / 4-5-1 — 4-2-3-1 is most common
    '3-5-2': '3-5-2',
    '3-4-3': '3-4-3',
    '5-3-2': '5-3-2',
    '3-4-2-1': '3-4-2-1', // never hits direct (counts to 3-4-3) — fallback handles it
    '5-4-1': '5-4-1',
  };
  if (key in DIRECT) return DIRECT[key];

  // Fallback: nearest-neighbour by absolute difference in lines.
  // Standard 11v11 shapes we know about.
  const CANDIDATES: Array<{ code: string; d: number; m: number; f: number }> = [
    { code: '4-3-3', d: 4, m: 3, f: 3 },
    { code: '4-4-2', d: 4, m: 4, f: 2 },
    { code: '4-2-3-1', d: 4, m: 5, f: 1 },
    { code: '3-5-2', d: 3, m: 5, f: 2 },
    { code: '3-4-3', d: 3, m: 4, f: 3 },
    { code: '5-3-2', d: 5, m: 3, f: 2 },
    { code: '4-1-4-1', d: 4, m: 5, f: 1 },
    { code: '3-4-2-1', d: 3, m: 4, f: 3 },
  ];
  let best: { code: string; dist: number } | null = null;
  for (const c of CANDIDATES) {
    const dist =
      Math.abs(c.d - defenders) +
      Math.abs(c.m - midfielders) +
      Math.abs(c.f - forwards);
    if (best === null || dist < best.dist) best = { code: c.code, dist };
  }
  // Only return when we're a close match (off by at most 1 in one
  // line). Otherwise the layout doesn't resemble a standard shape.
  return best && best.dist <= 1 ? best.code : null;
}
